/**
 * Patches Node.prototype.removeChild and Node.prototype.insertBefore to
 * gracefully handle NotFoundError caused by third-party scripts (e.g. New Relic,
 * Google Tag Manager) mutating the DOM outside React's control.
 *
 * @see https://github.com/facebook/react/issues/17256
 */

const originalRemoveChild = Node.prototype.removeChild;

Node.prototype.removeChild = function <T extends Node>(child: T): T {
  try {
    return originalRemoveChild.call(this, child) as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") {
      console.warn("[DOM Patch] Suppressed NotFoundError in removeChild — likely caused by third-party script DOM mutation");

      return child;
    }

    throw error;
  }
};

const originalInsertBefore = Node.prototype.insertBefore;

Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
  try {
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") {
      console.warn("[DOM Patch] Suppressed NotFoundError in insertBefore — likely caused by third-party script DOM mutation");

      return newNode;
    }

    throw error;
  }
};

/**
 * Patches Element.prototype.matches so that browsers which don't support
 * :focus-visible (Safari 14 / macOS Big Sur) don't throw when xyflow
 * internally calls element.matches(':focus-visible').
 */
const originalMatches = Element.prototype.matches;

Element.prototype.matches = function (selector: string): boolean {
  try {
    return originalMatches.call(this, selector);
  } catch (error) {
    if (selector === ":focus-visible") {
      return originalMatches.call(this, ":focus");
    }

    throw error;
  }
};

/**
 * Wraps ResizeObserver so its callback runs on the next animation frame instead
 * of synchronously. This eliminates the benign "ResizeObserver loop completed
 * with undelivered notifications" error at the source: that error fires when a
 * callback mutates layout and re-triggers observation within the same frame
 * (common with xyflow's NodeResizer + a resizing child). Deferring to rAF lets
 * the browser deliver all notifications on the next frame, so the loop condition
 * never arises. Suppressing it at the window-error level is unreliable here
 * because New Relic's wrapper re-dispatches to CRA's overlay regardless.
 *
 * @see https://github.com/WICG/resize-observer/issues/38
 */
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const OriginalResizeObserver = window.ResizeObserver;

  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

export {};
